// =============================
// File: src/components/user/DocsCarousel.jsx (UPDATED)
// =============================
import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, BookOpen, ArrowRight, ExternalLink } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

function DocsCarousel({ role = 'customer' }) {
  const [featuredDocs, setFeaturedDocs] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  // Auto-advance timer (7 seconds per card)
  useEffect(() => {
    if (featuredDocs.length === 0) return;

    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % featuredDocs.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [featuredDocs.length]);

  // Fetch featured docs
  useEffect(() => {
    async function fetchFeaturedDocs() {
      try {
        const response = await axios.get(`${API_URL}/api/docs`, {
          params: { role, featured: 'true' }
        });
        setFeaturedDocs(response.data);
      } catch (error) {
        console.error('Error fetching featured docs:', error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchFeaturedDocs();
  }, [role]);

  function handlePrevious() {
    setCurrentIndex((prev) => (prev - 1 + featuredDocs.length) % featuredDocs.length);
  }

  function handleNext() {
    setCurrentIndex((prev) => (prev + 1) % featuredDocs.length);
  }

  function handleCardClick(slug) {
    // Open in new window/tab
    window.open(`/#/user-docs/${slug}`, '_blank', 'noopener,noreferrer');
  }

  if (isLoading) {
    return (
      <div className="bg-gradient-to-r from-brand-primary/10 to-brand-gold/10 rounded-2xl p-6 mb-6 animate-pulse">
        <div className="h-24 bg-white/50 rounded-xl"></div>
      </div>
    );
  }

  if (featuredDocs.length === 0) {
    return null; // Don't show anything if no featured docs
  }

  const currentDoc = featuredDocs[currentIndex];

  return (
    <div className="relative mb-6">
      {/* Main Card */}
      <div className="bg-gradient-to-br from-white to-slate-50 rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
        {/* Header Badge */}
        <div className="bg-gradient-to-r from-brand-primary to-brand-gold px-4 py-2">
          <div className="flex items-center gap-2 justify-center">
            <BookOpen className="w-4 h-4 text-white" />
            <span className="text-xs font-bold text-white font-montserrat uppercase tracking-wide">
              Novedades Fast Max
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="flex items-start gap-4">
            {/* Icon */}
            <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-brand-primary to-brand-gold flex items-center justify-center shadow-md">
              <BookOpen className="w-6 h-6 text-white" />
            </div>

            {/* Text Content */}
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-extrabold text-slate-900 font-montserrat mb-2 line-clamp-2">
                {currentDoc.title}
              </h3>
              <p className="text-sm text-slate-600 font-montserrat line-clamp-2 mb-3">
                {currentDoc.short_description}
              </p>

              {/* CTA Button */}
              <button
                onClick={() => handleCardClick(currentDoc.slug)}
                className="inline-flex items-center gap-2 text-sm font-semibold text-brand-primary hover:text-brand-primary/80 transition-colors font-montserrat group"
              >
                <span>Ver más</span>
                <ExternalLink className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </button>
            </div>
          </div>
        </div>

        {/* Navigation Controls */}
        {featuredDocs.length > 1 && (
          <>
            {/* Arrow Buttons */}
            <div className="absolute top-1/2 -translate-y-1/2 left-2 right-2 flex justify-between pointer-events-none">
              <button
                onClick={handlePrevious}
                className="pointer-events-auto w-8 h-8 rounded-full bg-white/90 hover:bg-white shadow-lg flex items-center justify-center transition-all hover:scale-110 border border-slate-200"
                aria-label="Anterior"
              >
                <ChevronLeft className="w-5 h-5 text-slate-700" />
              </button>
              <button
                onClick={handleNext}
                className="pointer-events-auto w-8 h-8 rounded-full bg-white/90 hover:bg-white shadow-lg flex items-center justify-center transition-all hover:scale-110 border border-slate-200"
                aria-label="Siguiente"
              >
                <ChevronRight className="w-5 h-5 text-slate-700" />
              </button>
            </div>

            {/* Dots Indicator */}
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
              {featuredDocs.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`transition-all rounded-full ${
                    index === currentIndex
                      ? 'w-6 h-2 bg-brand-primary'
                      : 'w-2 h-2 bg-slate-300 hover:bg-slate-400'
                  }`}
                  aria-label={`Ir a documento ${index + 1}`}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default DocsCarousel;